import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackingStationsApiAdapter } from '../adapters/PackingStationsApiAdapter';
import { Service } from '../services/Service';
import { getApiErrorMessage } from '../utils/getApiErrorMessage';

// Hook para gestionar las relaciones de un puesto de packing (impresoras, clientes, ubicaciones)
export const useManagePackingStationRelations = (stationId: number, onSuccess?: () => void) => {
  const { t } = useTranslation('packingStationsModule');
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new PackingStationsApiAdapter(httpClient);
  const service = new Service(apiAdapter);

  // Añadir impresora al puesto
  const addPrinter = async (printerCode: string, type: '10x15' | 'a4', printerId?: number) => {
    setIsLoading(true);
    try {
      await service.addPrinter(stationId, {
        printer_code: printerCode,
        ...(printerId ? { printer_id: printerId } : {}),
        type,
      });
      toast({ title: t('toasts.addPrinter.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.addPrinter.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Quitar impresora del puesto
  const removePrinter = async (printerRelationId: number) => {
    setIsLoading(true);
    try {
      await service.removePrinter(stationId, printerRelationId);
      toast({ title: t('toasts.removePrinter.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.removePrinter.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Añadir cliente al puesto
  const addClient = async (tenantExternalId: number) => {
    setIsLoading(true);
    try {
      await service.addClient(stationId, { tenant_external_id: tenantExternalId });
      toast({ title: t('toasts.addClient.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.addClient.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Quitar cliente del puesto
  const removeClient = async (tenantExternalId: number) => {
    setIsLoading(true);
    try {
      await service.removeClient(stationId, tenantExternalId);
      toast({ title: t('toasts.removeClient.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.removeClient.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Añadir ubicación al puesto
  const addLocation = async (code: string) => {
    setIsLoading(true);
    try {
      await service.addLocation(stationId, { code });
      toast({ title: t('toasts.addLocation.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.addLocation.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Quitar ubicación del puesto (eliminando la location global)
  const removeLocation = async (locationId: number) => {
    setIsLoading(true);
    try {
      await service.deleteLocation(locationId);
      toast({ title: t('toasts.removeLocation.success.title'), variant: 'default' });
      onSuccess?.();
    } catch (error: unknown) {
      toast({
        title: t('toasts.removeLocation.error.title'),
        description: getApiErrorMessage(error, t('toasts.create.error.description')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    addPrinter,
    removePrinter,
    addClient,
    removeClient,
    addLocation,
    removeLocation,
  };
};
